import { useCallback, useEffect, useState } from "react";
import { api } from "../api";
import FileLink from "../components/FileLink";
import MessagePreview from "../components/MessagePreview";
// The three files a version can be downloaded as, and the one sentence
// saying which of them a checksum belongs to — written once, over there.
import { ArchivalCopy } from "./ProjectDocumentsCard";
import { Badge, Btn, Card, ConfirmDialog, Field, Input, Select, SrOnly, Textarea, Toggle, Spinner, formatWhen,
         Sheet, SheetActions, SheetHeader, RowActionsMenu } from "../components/Shared";
import { PersonLabel, personText } from "../components/Portal";
import { useDisplayTimezone, usePortalConfig } from "../portalConfig";
import { ROLE_LABELS, ROLE_ORDER, isGlobalAdmin } from "../auth/roles";
import { useAuth } from "../auth/AuthContext";

// Policies, terms and information sheets a facility publishes.
//
// A notice is just published. An acknowledged document is tracked exactly as a
// quotation is — same table, same evidence — which is what makes "monitored the
// same way" true by construction rather than by discipline.

const CATEGORIES = [
  { value: "policy", label: "Policy" },
  { value: "terms",  label: "Terms and conditions" },
  { value: "info",   label: "Information" },
  { value: "sop",    label: "SOP" },
];

function VersionHistory({ groupId, document: doc, onClose, onAsk }) {
  const [rows, setRows] = useState(null);
  const [against, setAgainst] = useState("");
  const [diff, setDiff] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const { user, refreshDuties } = useAuth();
  const fourEyes = useFourEyes();

  const load = useCallback(() => {
    api.get(`/api/groups/${groupId}/documents/${doc.id}/versions`)
      .then(setRows).catch(e => setError(e.message));
  }, [groupId, doc.id]);
  useEffect(load, [load]);

  // Only a global admin can approve, and never their own version — the rule is
  // two people, not two roles, so an account holding both grants gets neither
  // button on something it composed. Mirrored here so nobody is offered an
  // action that will be refused; the server is what enforces it.
  // By id, never by name: two accounts may share one (PERSON, 2026-10-06).
  const own = v => user?.id != null && v.author_id === user.id;

  // *Take back* goes to the reject route: the server sees the caller is the
  // author and records `document.retract`, not a rejection (2026-10-30).
  const decide = async (n, verb) => {
    const note = verb === "reject"
      ? window.prompt("Why is this version being rejected? The author has to act on it.")
      : verb === "take-back"
      ? window.prompt("Why are you taking this version back? The log keeps the reason.")
      : window.prompt("A note about this approval (optional)") ?? "";
    if (verb !== "approve" && !(note || "").trim()) return;
    const route = verb === "take-back" ? "reject" : verb;
    setBusy(true); setError(null);
    try {
      await api.post(
        `/api/groups/${groupId}/documents/${doc.id}/versions/${n}/${route}`, { note });
      load();
      // Deciding a version takes it off this admin's list of things to approve,
      // which the banner and the badge count (DUTIES, 2026-10-06).
      refreshDuties?.();
    } catch (e) { setError(e.message); } finally { setBusy(false); }
  };

  const compare = async b => {
    setError(null);
    setDiff(null);
    try {
      const q = b ? `?against=${b}` : "";
      setDiff(await api.get(
        `/api/groups/${groupId}/documents/${doc.id}/versions/${doc.version}/diff${q}`));
    } catch (e) { setError(e.message); }
  };

  // A sheet, not a panel inside the card (2026-09-30): at 390px the panel was
  // squeezed to about 110px, with Version and Published cut off. The table
  // reads as cards on a phone, one per version.
  return (
    <Sheet labelledBy={`history-${doc.id}-t`} onClose={onClose} maxWidth={960} variant="full">
      <SheetHeader id={`history-${doc.id}-t`} title={`${doc.title} · history`} onClose={onClose} />
      {error && <div role="alert" style={{ marginTop: 8, color: "#b91c1c", fontSize: 13 }}>{error}</div>}

      {rows === null ? <div style={{ color: "#6b7280", marginTop: 8 }}><Spinner /> Loading…</div> : (
        <table className="responsive-table"
               style={{ borderCollapse: "collapse", width: "100%", fontSize: 13, marginTop: 10 }}>
          <thead>
            <tr style={{ borderBottom: "1px solid #e5e7eb", textAlign: "left" }}>
              <th style={{ padding: "6px 8px" }}>Version</th>
              <th style={{ padding: "6px 8px" }}>Published</th>
              <th style={{ padding: "6px 8px" }}>What changed</th>
              <th style={{ padding: "6px 8px" }}>Signatures</th>
              {/* The panel has received `content_hash`, `artifact_hash` and now
                  `original_md5` for every version since it was written, and
                  rendered none of them — so the one screen that can tell the
                  versions apart could not say what to check. */}
              <th style={{ padding: "6px 8px" }}>The file</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {rows.map(v => (
              <tr key={v.version} style={{ borderBottom: "1px solid #f3f4f6" }}>
                <td data-label="Version" style={{ padding: "6px 8px" }}>
                  v{v.version}{v.current && <> <Badge color="green">in force</Badge></>}
                  {v.approval_state === "pending" && <> <Badge color="amber">waiting for approval</Badge></>}
                  {v.approval_state === "rejected" && <> <Badge color="red">rejected</Badge></>}
                </td>
                <td data-label="Published" style={{ padding: "6px 8px", color: "#6b7280" }}>{formatWhen(v.published_at, { zone: undefined })}</td>
                <td data-label="What changed" style={{ padding: "6px 8px" }}>{v.change_note || <span style={{ color: "#9ca3af" }}>—</span>}</td>
                <td data-label="Signatures" style={{ padding: "6px 8px", color: "#6b7280" }}>{v.signatures}</td>
                <td data-label="The file" style={{ padding: "6px 8px" }}>
                  {/* `?version=` names THIS version rather than the one in
                      force, which is the whole point of downloading from a
                      history. `content_hash` is the sha256, because that is the
                      value `Acknowledgement.subject_hash` is written from and
                      compared against — `artifact_hash` covers the render.

                      The md5 is the API's own, stored beside the bytes at
                      upload. A Markdown version has none and says so: it is
                      rendered at download, and re-rendering it now to produce
                      one would freeze this year's renderer over a document
                      published under another. */}
                  <ArchivalCopy
                    path={`/api/groups/${groupId}/documents/${doc.id}/pdf?version=${v.version}`}
                    md5={v.original_md5} sha256={v.content_hash}
                    absent={v.source_kind === "pdf"
                      ? "No md5 was recorded for this version."
                      : "This version is rendered at download, so no md5 is published for it."} />
                </td>
                <td style={{ padding: "6px 8px", textAlign: "right" }}>
                  {/* A version published before approval was required shows what
                      it is rather than a blank — a blank reads as "nobody has
                      looked at this", which is not what happened. */}
                  {v.approval_state === "legacy" && (
                    <span style={{ fontSize: 12, color: "#6b7280", marginRight: 8 }}>
                      published before approval was required
                    </span>
                  )}
                  {v.approval_state === "approved" && v.approved_by && (
                    <span style={{ fontSize: 12, color: "#6b7280", marginRight: 8 }}>
                      approved by <PersonLabel {...v.approved_by} />
                    </span>
                  )}
                  {v.approval_state === "pending" && isGlobalAdmin(user)
                    && !(fourEyes && own(v)) && (
                    <Btn variant="secondary" disabled={busy}
                         onClick={() => decide(v.version, "approve")}
                         style={{ padding: "3px 9px", fontSize: 12, marginRight: 6 }}>Approve</Btn>
                  )}
                  {/* Reject is a second person saying no; on one's own version
                      it is *Take back* below, which the server records as such. */}
                  {v.approval_state === "pending" && isGlobalAdmin(user) && !own(v) && (
                    <Btn variant="danger" disabled={busy}
                         onClick={() => decide(v.version, "reject")}
                         style={{ padding: "3px 9px", fontSize: 12, marginRight: 6 }}>Reject</Btn>
                  )}
                  {/* The author takes their own pending version back (2026-10-30):
                      approving needs a second person, withdrawing one's own
                      proposal does not — nobody agreed to it, nothing goes into
                      force. Not called *Withdraw*: on this screen that archives a
                      policy in force. */}
                  {v.approval_state === "pending" && own(v) && (
                    <Btn variant="secondary" disabled={busy}
                         onClick={() => decide(v.version, "take-back")}
                         style={{ padding: "3px 9px", fontSize: 12, marginRight: 6 }}>Take back my version</Btn>
                  )}
                  {/* The author gets neither, on their own version, however many
                      grants they hold — said on screen rather than only as a 403.
                      By id, never by name: two accounts may share one (PERSON). */}
                  {/* Not while four eyes is off: then the author may clear their
                      own leftover, and the server agrees (2026-10-14). */}
                  {v.approval_state === "pending" && isGlobalAdmin(user) && fourEyes && own(v) && (
                    <span style={{ fontSize: 12, color: "#92400e", marginRight: 8 }}>
                      you composed this — another administrator has to approve it
                    </span>
                  )}
                  {/* *Ask for approval* on the version that waits for it, for
                      whoever may edit the group's documents — the same action
                      as on the row, naming this version. */}
                  {v.approval_state === "pending" && onAsk && (
                    <Btn variant="secondary" onClick={() => onAsk(v.version)}
                         style={{ padding: "3px 9px", fontSize: 12, marginRight: 6 }}>Ask for approval</Btn>
                  )}
                  {/* Only for the version in force, and only against another
                      one: "compare with" is the question people actually ask,
                      and it is always asked from the current text. */}
                  {!v.current && v.source_kind !== "pdf" && (
                    <button type="button" onClick={() => { setAgainst(String(v.version)); compare(v.version); }}
                            style={{ background: "none", border: "none", cursor: "pointer", color: "#374151", fontSize: 12, fontFamily: "inherit", textDecoration: "underline" }}>
                      compare with v{doc.version}
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {/* data-html: the mobile rules wrap it and scroll a wide line inside the
          box, as they do for a document's own rendered text. */}
      {diff && (
        <div data-html style={{ marginTop: 12 }}>
          <div style={{ fontSize: 12, color: "#6b7280", marginBottom: 6 }}>
            v{diff.b} → v{diff.a}
          </div>
          <pre style={{ margin: 0, background: "#fff", border: "1px solid #e5e7eb", borderRadius: 8, padding: "10px 12px", fontSize: 12.5, overflowX: "auto", fontFamily: "'JetBrains Mono', monospace" }}>
            {diff.hunks.map((h, i) => (
              <div key={i} style={{
                background: h.op === "added" ? "rgba(0,150,64,0.10)"
                          : h.op === "removed" ? "rgba(220,38,38,0.08)" : "transparent",
                color: h.op === "equal" ? "#6b7280" : "#1a1a1a",
              }}>
                {/* A symbol as well as the colour: WCAG 1.4.1, and this is
                    exactly the shape the screen-reader pass found on the
                    sign-in chooser. */}
                <span aria-hidden="true">{h.op === "added" ? "+ " : h.op === "removed" ? "− " : "  "}</span>
                {h.op !== "equal" && (
                  <SrOnly>{h.op === "added" ? "added: " : "removed: "}</SrOnly>
                )}
                {h.text || "\u00a0"}
              </div>
            ))}
          </pre>
          {diff.hunks.every(h => h.op === "equal") && (
            <p style={{ fontSize: 13, color: "#6b7280", marginTop: 8 }}>
              These two versions have identical text.
            </p>
          )}
        </div>
      )}
      {against && !diff && !error && <div style={{ marginTop: 10, color: "#6b7280" }}><Spinner /> Comparing…</div>}
    </Sheet>
  );
}

// Whether document acts wait for a second administrator (`document_four_eyes`,
// Portal settings, switchable since 2026-10-14). `!== false`: a server that
// does not send it yet, and the fallback before the config arrives, are ON —
// the side that promises nothing the server would not do. The server stays the
// gate; this only decides what the screen says will happen.
function useFourEyes() {
  return usePortalConfig().document_four_eyes !== false;
}

function DocumentEditor({ groupId, document: doc, onSaved, onCancel }) {
  const [markdown, setMarkdown] = useState("");
  const [note, setNote] = useState("");
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  // A document is one kind of thing: written here, or uploaded. The server
  // refuses to mix them in one history, and this is the same rule on screen so
  // nobody meets it as a 409 after choosing a file.
  const [kind, setKind] = useState(doc.source_kind || "markdown");
  const [file, setFile] = useState(null);
  // What the last press actually did. The preview cannot say it: it renders the
  // version IN FORCE, which is empty for a document waiting on approval — so
  // the only feedback a publish gave was a block that disappeared, and somebody
  // pressed the button six times.
  const [submitted, setSubmitted] = useState(null);
  const fourEyes = useFourEyes();

  useEffect(() => {
    api.get(`/api/groups/${groupId}/documents/${doc.id}`)
      .then(d => {
        // **`latest`, falling back to the version in force.** A document whose
        // only versions are pending has nothing in force, and loading only that
        // gave a group admin an EMPTY textarea over their own stored text — the
        // "ani po editaci markdown" half of the 2026-09-08 report.
        const body = d.latest || d;
        setMarkdown(body.markdown || "");
        setPreview(body.html || null);
        if (body.source_kind || d.source_kind) setKind(body.source_kind || d.source_kind);
      })
      .catch(e => setError(e.message));
  }, [groupId, doc.id]);

  const upload = async () => {
    setBusy(true); setError(null);
    try {
      const body = new FormData();
      body.append("file", file);
      if (note) body.append("change_note", note);
      const out = await api.upload(
        `/api/groups/${groupId}/documents/${doc.id}/versions/upload`, body);
      setPreview((out.latest && out.latest.html) || out.html || null);
      setSubmitted(out);
      setNote("");
      setFile(null);
      onSaved(out);
    } catch (e) { setError(e.message); } finally { setBusy(false); }
  };

  const publish = async () => {
    setBusy(true); setError(null);
    try {
      const out = await api.post(`/api/groups/${groupId}/documents/${doc.id}/versions`,
                                 { markdown, change_note: note || null, publish: true });
      setPreview((out.latest && out.latest.html) || out.html || null);
      setSubmitted(out);
      setNote("");
      onSaved(out);
    } catch (e) { setError(e.message); } finally { setBusy(false); }
  };

  // A sheet (2026-09-30): inside the card the preview was squeezed to one word
  // per line and its table widened the page to 1 292px — the worst screen
  // measured. Closed by the "×"; there is nothing to cancel once published.
  const what = doc.source_kind === "pdf" ? "New version" : "Edit text";
  return (
    <Sheet labelledBy={`editor-${doc.id}-t`} onClose={onCancel} maxWidth={860} variant="full">
      <SheetHeader id={`editor-${doc.id}-t`} title={`${what} · ${doc.title}`} onClose={onCancel} />
      {/* A refusal for want of a second administrator is an INSTRUCTION, not a
          failure: nothing is wrong with what was written, and the facility is
          told what to do about it. Rendered as such rather than in red, which
          reads as "your text was rejected". Still role="alert" — it is the
          answer to the button that was just pressed. */}
      {error && (
        /needs?\s|second administrator|already waiting for approval/i.test(error) ? (
          <div role="alert" style={{ marginBottom: 12, padding: "10px 12px", borderRadius: 8, background: "rgba(217,119,6,0.08)", border: "1px solid rgba(217,119,6,0.35)", fontSize: 13, color: "#92400e" }}>
            {error}
          </div>
        ) : (
          <div role="alert" style={{ marginBottom: 12, color: "#b91c1c", fontSize: 13 }}>{error}</div>
        )
      )}

      {/* Said in words, because the button looks identical whether it worked or
          not and the preview is empty in exactly the case that needs saying. */}
      {/* Decided by what the server DID — the version's own state in the
          response — not by the switch as this page last read it. */}
      {submitted && submitted.submitted && submitted.submitted.approval_state === "approved" && (
        <div role="status" style={{ marginBottom: 12, padding: "10px 12px", borderRadius: 8, background: "rgba(22,163,74,0.08)", border: "1px solid rgba(22,163,74,0.35)", fontSize: 13, color: "#166534" }}>
          <strong>Version {submitted.submitted.version} is in force.</strong>{" "}
          Four eyes on documents is switched off, so it took effect without a second administrator,
          and the log says so.
        </div>
      )}
      {submitted && submitted.submitted && submitted.submitted.approval_state !== "approved" && (
        <div role="status" style={{ marginBottom: 12, padding: "10px 12px", borderRadius: 8, background: "rgba(217,119,6,0.08)", border: "1px solid rgba(217,119,6,0.35)", fontSize: 13, color: "#92400e" }}>
          <strong>Version {submitted.submitted.version} submitted.</strong>{" "}
          {submitted.pending && submitted.pending.eligible_approvers === 0 ? (
            <>It is <strong>not in force</strong>, and there is no second administrator who could
            approve it — you composed it, and nobody may approve their own. Nothing is asked of
            anybody until somebody else approves this.</>
          ) : (
            <>It is <strong>not in force</strong> until an administrator approves it. Nothing is
            asked of anybody until then.</>
          )}
        </div>
      )}

      {/* Offered only for a document with no versions yet. Once one exists the
          kind is settled, and showing a choice that will be refused is worse
          than showing none. */}
      {!doc.version && (
        <Field label="Written here, or uploaded"
               hint="A document is one or the other for its whole history. Text can be edited, compared version to version, and rendered to PDF by the portal; an uploaded file is kept and hashed exactly as it arrives.">
          <Select value={kind} onChange={e => setKind(e.target.value)}
                  options={[{ value: "markdown", label: "Written here (Markdown)" },
                            { value: "pdf", label: "An uploaded PDF" }]} />
        </Field>
      )}

      {kind === "markdown" ? (
        <Field label="Text (Markdown)"
               hint="Published as a new version — the previous one is kept, so an acknowledgement never points at words that were changed underneath it.">
          <Textarea rows={14} value={markdown} onChange={e => setMarkdown(e.target.value)}
                    style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 13 }} />
        </Field>
      ) : (
        <Field label="PDF file"
               hint="Stored and hashed exactly as it arrives, so the file people download is the file you uploaded. It cannot be edited here and cannot be compared with another version line by line — a correction is a new upload. Up to 10 MB, and it must not be password-protected.">
          <input type="file" accept="application/pdf"
                 onChange={e => setFile(e.target.files?.[0] || null)} />
        </Field>
      )}

      <Field label="What changed" hint="Shown in the log beside the version number">
        <Input value={note} onChange={e => setNote(e.target.value)}
               placeholder="Corrected the address" />
      </Field>

      {!fourEyes && (
        <p style={{ fontSize: 12, color: "#6b7280", margin: "0 0 8px" }}>
          Four eyes on documents is switched off: a new version is <strong>in force as soon as you
          publish it</strong>, without a second administrator.
        </p>
      )}
      <SheetActions style={{ marginTop: 4 }}>
        {kind === "markdown" ? (
          <Btn onClick={publish} disabled={busy || !markdown.trim()}>
            {busy ? <><Spinner /> Publishing…</> : "Publish new version"}
          </Btn>
        ) : (
          <Btn onClick={upload} disabled={busy || !file}>
            {busy ? <><Spinner /> Uploading…</> : "Upload new version"}
          </Btn>
        )}
      </SheetActions>

      {preview && (
        <div style={{ marginTop: 16 }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: "#374151", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 6 }}>
            Published version
          </div>
          {/* Rendered and sanitised on the server, so the page, the PDF and the
              hash all describe the same text. */}
          <div data-html style={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: 8, padding: "12px 16px", fontSize: 14 }}
               dangerouslySetInnerHTML={{ __html: preview }} />
        </div>
      )}
    </Sheet>
  );
}

// The two target pickers, and the sentence that keeps them from being used
// interchangeably.
//
// They look alike and they are not alike, which is the whole risk: naming
// projects is a LIST and a project created next month is not on it; naming an
// affiliation is a RULE and a new project carries its affiliation on the day it
// is made. A facility that treats them as two ways of saying the same thing will
// pick the list, add a project next month, and never learn that the policy
// missed it. So the difference is written where the choice is made, not in a
// manual.
function TargetPicker({ label, hint, options, selected, onToggle, empty }) {
  if (options === null) {
    return <Field label={label} hint={hint}><span style={{ color: "#6b7280", fontSize: 13 }}><Spinner /> Loading…</span></Field>;
  }
  // Alphabetical, here rather than at the two call sites — one rule in one place
  // is what stops the editor and the new-document form drifting apart.
  //
  // The APIs each have their own order and both are right for what they are for:
  // /api/projects is newest-first, which is what a project TABLE wants, and
  // /api/affiliations is the facility's own `sort_order`, which is how it reads
  // its rate table. Neither is any use in a checkbox list somebody is looking a
  // name up in, and a facility with dozens of projects was scanning instead of
  // reading.
  //
  // `slice()` first: the array belongs to the caller. Both call sites happen to
  // pass a freshly mapped one today, so sorting in place would work and would
  // stop working silently the moment one of them memoises.
  //
  // `localeCompare`, not `<`: the affiliation codes are `_§26 / FWF_§27` and the
  // project names are German and English, so a bare comparison gets `§`, `Ä` and
  // case wrong.
  const shown = options.slice().sort((a, b) => String(a.label).localeCompare(String(b.label)));
  return (
    <Field label={label} hint={hint}>
      {options.length === 0 ? (
        <span style={{ color: "#6b7280", fontSize: 13 }}>{empty}</span>
      ) : (
        <div style={{ maxHeight: 132, overflowY: "auto", border: "1px solid #e5e7eb",
                      borderRadius: 6, padding: "6px 8px", background: "#fff" }}>
          {shown.map(o => (
            <label key={o.id} style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, padding: "2px 0" }}>
              <input type="checkbox" checked={selected.includes(o.id)}
                     onChange={() => onToggle(o.id)} />
              {/* A project name or an affiliation code with nowhere to break
                  (`_§26 / FWF_§27`, a sample ID) breaks anyway, inside the box. */}
              <span data-break>{o.label}</span>
            </label>
          ))}
        </div>
      )}
    </Field>
  );
}


// ── Who confirms, per role (2026-09-29) ─────────────────────────────────────
//
// One rule per role — never, once, or per project — which is what
// `Document.confirm_rules` stores and `documents.must_confirm` on the server
// reads. It replaced the role checkboxes and the "Confirmed" select, which could
// only say ONE cadence for everybody: the usage policy is once for an operator
// and on every project for a researcher, and no pair of those controls could
// say that. A role left out is never asked; the server has no third value.

const RULE_CHOICES = [
  { value: "never",       label: "Never" },
  { value: "once",        label: "Once" },
  { value: "per_project", label: "Per project" },
];

// What an empty audience with a group scope has always meant, and what the
// server's `confirm_rules_from([], "group")` writes: everybody with standing in
// the group, once — not a global admin, who was never asked of a document that
// did not name the role.
const DEFAULT_RULES = { admin: "never", group_admin: "once", operator: "once", user: "once" };

// The rules a document has, as `{role: "never" | "once" | "per_project"}`.
// The server sends them resolved; a row without them is read the way the
// server reads one, from `scope` alone — `audience` is retired and no longer
// sent — so the dialog never opens on rules the server does not hold.
function rulesOf(doc) {
  const out = Object.fromEntries(ROLE_ORDER.map(r => [r, "never"]));
  if (Array.isArray(doc.confirm_rules)) {
    for (const r of doc.confirm_rules) {
      if (r && ROLE_ORDER.includes(r.role) && (r.scope === "once" || r.scope === "per_project")) {
        out[r.role] = r.scope;
      }
    }
    return out;
  }
  const cadence = doc.scope === "per_project" ? "per_project" : "once";
  for (const r of ROLE_ORDER) {
    if (r !== "admin") out[r] = cadence;
  }
  return out;
}

// The stored shape: `never` is left out, in ROLE_ORDER — the canonical list the
// server compares with, so a reordering is not proposed as a change.
function rulesAsList(rules) {
  return ROLE_ORDER.filter(r => rules[r] === "once" || rules[r] === "per_project")
    .map(r => ({ role: r, scope: rules[r] }));
}

const hasPerProject = rules => ROLE_ORDER.some(r => rules[r] === "per_project");

function describeRules(rules) {
  const words = ROLE_ORDER.filter(r => rules[r] !== "never")
    .map(r => `${ROLE_LABELS[r].toLowerCase()} ${rules[r] === "once" ? "once" : "per project"}`);
  return words.length ? words.join(", ") : "nobody";
}

// One block per role, each choice a row of its own (2026-09-30). It was a table
// of four columns, which in a 560px dialog at 390px ran Never, Once and Per
// project off the screen's edge. A fieldset per role says the role once, as the
// block's name; each radio's own name still carries the role, because that is
// how a screen reader announces it on its own and how the tests reach it. On a
// phone data-form-grid stacks the blocks, and a label holding a radio is a 44px
// row by the mobile rules in Shared.jsx.
function RoleRules({ name, rules, onChange }) {
  const legend = { fontSize: 12, fontWeight: 600, color: "#374151", padding: 0 };
  return (
    <fieldset style={{ border: "none", margin: 0, padding: 0, minWidth: 0 }}>
      <legend style={{ ...legend, letterSpacing: "0.05em", textTransform: "uppercase",
                       marginBottom: 6 }}>
        Who confirms, and how often
      </legend>
      <div data-form-grid
           style={{ display: "grid", gridTemplateColumns: "repeat(4, minmax(0, 1fr))", gap: 8 }}>
        {ROLE_ORDER.map(r => (
          <fieldset key={r} style={{ border: "1px solid #e5e7eb", borderRadius: 8, margin: 0,
                                     padding: "6px 10px 8px", minWidth: 0 }}>
            <legend style={{ ...legend, fontSize: 13, color: "#1a1a1a", padding: "0 4px" }}>
              {ROLE_LABELS[r]}
            </legend>
            {RULE_CHOICES.map(c => (
              <label key={c.value} style={{ display: "flex", alignItems: "center", gap: 8,
                                            fontSize: 13, padding: "3px 0", cursor: "pointer" }}>
                <input type="radio" name={`${name}-${r}`} value={c.value}
                       aria-label={`${ROLE_LABELS[r]}: ${c.label.toLowerCase()}`}
                       checked={rules[r] === c.value}
                       onChange={() => onChange({ ...rules, [r]: c.value })} />
                {c.label}
              </label>
            ))}
          </fieldset>
        ))}
      </div>
    </fieldset>
  );
}

// The project and affiliation pickers, under the heading that says what they
// narrow: a per-project rule only. A `once` confirmation belongs to no project,
// so naming projects cannot limit it (the maintainer, 2026-09-29) — and the
// server refuses targets on a document with no per-project rule at all.
function PerProjectTargets({ rules, projects, affiliations, projectIds, affiliationIds,
                             onToggleProject, onToggleAffiliation, projectHint, affiliationHint,
                             noAffiliations }) {
  if (!hasPerProject(rules)) {
    return (
      <p style={{ fontSize: 12, color: "#6b7280", margin: "10px 0 0" }}>
        No role confirms per project, so there are no projects to name: a <em>once</em> confirmation
        is for the whole facility. Give a role <em>Per project</em> to limit it to named work.
      </p>
    );
  }
  return (
    <div style={{ marginTop: 12 }}>
      <div style={{ fontSize: 12, fontWeight: 600, color: "#374151", marginBottom: 6 }}>
        Per-project rules apply to
      </div>
      <div data-form-grid
           style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 12 }}>
        <TargetPicker label="Only these projects" hint={projectHint}
                      options={projects && projects.map(p => ({ id: p.id, label: p.name }))}
                      selected={projectIds} onToggle={onToggleProject}
                      empty="This group has no projects yet." />
        <TargetPicker label="Only these affiliations" hint={affiliationHint}
                      options={affiliations && affiliations.map(a => ({ id: a.id, label: a.label || a.code }))}
                      selected={affiliationIds} onToggle={onToggleAffiliation}
                      empty={noAffiliations} />
      </div>
      {projectIds.length === 0 && affiliationIds.length === 0 && (
        <p style={{ fontSize: 12, color: "#6b7280", margin: "6px 0 0" }}>
          Nothing named, so the per-project rules reach <strong>every project in this group</strong>.
        </p>
      )}
    </div>
  );
}


// Changing who an existing policy is for.
//
// The TEXT of a version is immutable and a correction to it is a new version —
// but a target is not text, and changing who a policy is asked of is an edit.
// It is deliberately NOT retroactive: a project already running when the
// document was published stays outside it however it is named afterwards,
// because its pin already decided. The dialog says so, or the first person to
// use it will report the pin as a bug.
//
// It posts the WHOLE document, because `PATCH /api/groups/{gid}/documents/{id}`
// sets every field from the payload it is given — the same shape the admin user
// form posts, and for the same reason.
function TargetEditor({ groupId, document: doc, projects, affiliations, onSaved, onCancel }) {
  const [projectIds, setProjectIds] = useState(doc.project_ids || []);
  const [affiliationIds, setAffiliationIds] = useState(doc.affiliation_ids || []);
  const [rules, setRules] = useState(() => rulesOf(doc));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const asks = doc.mode === "acknowledge";
  const fourEyes = useFourEyes();

  // `scope` goes back exactly as it is and `audience` not at all (retired): the
  // rules are what the server reads now, and changing the legacy field as well
  // would make the change request carry a second description of the same edit.
  // Targets are sent only while a per-project rule exists to narrow — the
  // server refuses them otherwise, and a picker hidden with ticks still in it
  // is not a choice anybody can see.
  const save = async () => {
    setBusy(true); setError(null);
    const narrowed = asks && hasPerProject(rules);
    try {
      await api.patch(`/api/groups/${groupId}/documents/${doc.id}`, {
        title: doc.title, category: doc.category, mode: doc.mode, scope: doc.scope,
        blocking: !!doc.blocking, due_days: doc.due_days,
        is_active: doc.status !== "archived",
        confirm_rules: asks ? rulesAsList(rules) : rulesAsList(rulesOf(doc)),
        project_ids: narrowed ? projectIds : [],
        affiliation_ids: narrowed ? affiliationIds : [],
      });
      onSaved();
    } catch (e) { setError(e.message); } finally { setBusy(false); }
  };

  const toggle = (setter) => (id) =>
    setter(cur => cur.includes(id) ? cur.filter(x => x !== id) : [...cur, id]);

  return (
    <ConfirmDialog title={`Who “${doc.title}” is for`} confirmLabel="Save"
                   busyLabel="Saving…" tone="default"
                   onConfirm={save} onClose={onCancel} busy={busy}>
      {error && <p role="alert" style={{ color: "#b91c1c", fontSize: 13 }}>{error}</p>}
      {!asks ? (
        <p style={{ fontSize: 13, color: "#374151", margin: 0 }}>
          “{doc.title}” is a <strong>notice</strong>: it asks nobody to confirm anything, so there
          are no rules to set. It is readable by everybody with standing in this facility.
        </p>
      ) : (
        <>
          <RoleRules name={`rules-${doc.id}`} rules={rules} onChange={setRules} />
          <PerProjectTargets rules={rules} projects={projects} affiliations={affiliations}
                             projectIds={projectIds} affiliationIds={affiliationIds}
                             onToggleProject={toggle(setProjectIds)}
                             onToggleAffiliation={toggle(setAffiliationIds)}
                             projectHint="A LIST — a project created later is not on it."
                             affiliationHint="A RULE — a project created later under one of these is covered automatically."
                             noAffiliations="This group has imported no price list." />
          <p style={{ fontSize: 12, color: "#6b7280", marginTop: 10 }}>
            {fourEyes ? (
              <>A change to who confirms is binding, so it is <strong>sent for approval</strong> rather
              than made: a second administrator agrees before it applies.</>
            ) : (
              <>A change to who confirms is binding. Four eyes on documents is switched off, so it
              <strong> applies at once</strong> when you save, and the log says so.</>
            )}
          </p>
        </>
      )}
      <p style={{ fontSize: 12, color: "#6b7280", marginTop: 10 }}>
        Projects that were already running when this policy was published are not affected either
        way. What they are subject to was decided when they started, and a policy appearing over
        work already under way is what that rule exists to prevent.
      </p>
    </ConfirmDialog>
  );
}


// How each application rule reads on screen. The wording is what a group admin
// has to be able to act on, so it says what is HELD rather than naming the
// mechanism: "work on it waits" is actionable, "per_project targeting" is not.
const APPLICATION_LABELS = {
  new_projects: "New projects only",
  projects:     "Named projects — work waits until everybody on each has signed",
  accounts:     "Named accounts — that person's own new work waits",
  groups:       "Everybody in this facility",
  affiliations: "Named affiliations — new projects under them are covered too",
};

// The create form's starting point. `rules` is the role table; a notice asks
// nobody, and is sent with the default rules only so the row is explicit.
// ── Send notification (2026-09-29) ───────────────────────────────────────────
//
// Nothing told a researcher that a policy waited for them until an upload was
// refused. The list here is the server's — `documents.outstanding`, the reader
// "Who has read it" and the upload gate share — and the server checks the ticked
// people against it again on send, so this dialog can narrow the audience and
// never widen it. One key per confirmation owed: a person can owe one on two
// projects, and each is its own message.
const notifyKey = r => `${r.user_id}:${r.project_id ?? ""}`;

// "Last notified" is the newest message the outbox marked sent — a queued one
// has reached nobody yet. A newer one still waiting, or one that failed, is
// said beside it rather than counted as delivered.
function notifiedLine(r, zone) {
  const sent = r.last_notified
    ? `Last notified ${formatWhen(r.last_notified, { zone })}` : "Never notified about this document";
  if (r.pending_notification === "queued") {
    return r.last_notified ? `${sent}; a newer one is queued, not sent yet`
      : "A notification is queued, not sent yet";
  }
  if (r.pending_notification === "failed") return `${sent}; the newest one failed to send`;
  return sent;
}
const canReach = r => Boolean((r.email || "").trim());

function NotifyDialog({ groupId, document: doc, onSent, onClose }) {
  const zone = useDisplayTimezone();
  const [rows, setRows] = useState(null);
  const [ticked, setTicked] = useState(() => new Set());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    api.get(`/api/groups/${groupId}/documents/${doc.id}/notify`)
      .then(data => {
        setRows(data.recipients);
        // Ticked by default — everybody who can be sent it.
        setTicked(new Set(data.recipients.filter(canReach).map(notifyKey)));
      })
      .catch(e => { setRows([]); setError(e.message); });
  }, [groupId, doc.id]);

  const toggle = r => setTicked(t => {
    const next = new Set(t);
    if (next.has(notifyKey(r))) next.delete(notifyKey(r)); else next.add(notifyKey(r));
    return next;
  });
  const chosen = (rows || []).filter(r => ticked.has(notifyKey(r)));
  const first = chosen[0];

  const send = async () => {
    setBusy(true); setError(null);
    try {
      const out = await api.post(`/api/groups/${groupId}/documents/${doc.id}/notify`, {
        recipients: chosen.map(r => ({ user_id: r.user_id, project_id: r.project_id ?? null })),
      });
      onSent(out.queued);
    } catch (e) { setError(e.message); } finally { setBusy(false); }
  };

  return (
    <Sheet labelledBy={`notify-${doc.id}-t`} onClose={onClose} maxWidth={620}>
        <h2 id={`notify-${doc.id}-t`} style={{ margin: "0 0 8px", fontSize: 18, fontWeight: 700, color: "#0b6b33" }}>
          Send notification: {doc.title}
        </h2>
        <p style={{ margin: "0 0 12px", fontSize: 13, color: "#374151" }}>
          Everybody this document is still waiting for. Each person ticked gets one e-mail with a
          link to the page where they confirm it — under their own sign-in, never a link that signs
          anyone in. The wording is the <em>Policy Waiting for Confirmation</em> template in
          E-mail and templates.
        </p>
        {error && <div role="alert" style={{ marginBottom: 12, padding: "10px 12px", borderRadius: 8, background: "rgba(220,38,38,0.06)", border: "1px solid rgba(220,38,38,0.25)", color: "#b91c1c", fontSize: 13 }}>{error}</div>}
        {rows === null ? <div style={{ color: "#6b7280" }}><Spinner /> Loading…</div>
          : rows.length === 0 ? (
            <p style={{ margin: 0, fontSize: 13, color: "#6b7280" }}>
              Nobody is waiting to confirm this document: everybody it asks has confirmed it.
            </p>
          ) : (
          <>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 8 }}>
              <Btn variant="secondary" style={{ padding: "3px 10px", fontSize: 12 }}
                   onClick={() => setTicked(new Set(rows.filter(canReach).map(notifyKey)))}>Select all</Btn>
              <Btn variant="secondary" style={{ padding: "3px 10px", fontSize: 12 }}
                   onClick={() => setTicked(new Set())}>Select none</Btn>
            </div>
            <div style={{ border: "1px solid #e5e7eb", borderRadius: 8, maxHeight: 240, overflowY: "auto", marginBottom: 14 }}>
              {rows.map(r => {
                const reachable = canReach(r);
                return (
                  <label key={notifyKey(r)}
                         style={{ display: "flex", gap: 10, alignItems: "flex-start", padding: "7px 10px", borderBottom: "1px solid #f3f4f6", fontSize: 13, cursor: reachable ? "pointer" : "default", color: reachable ? "#1a1a1a" : "#6b7280" }}>
                    <input type="checkbox" checked={ticked.has(notifyKey(r))} disabled={!reachable}
                           onChange={() => toggle(r)} style={{ marginTop: 2 }} />
                    {/* An e-mail address has no space to break at; on a phone it
                        breaks here rather than pushing the sheet wider. */}
                    <span data-break>
                      <strong>{r.full_name || r.email || `User ${r.user_id}`}</strong>
                      {reachable && <span style={{ color: "#6b7280" }}> · {r.email}</span>}
                      {r.project && <span style={{ color: "#6b7280" }}> · {r.project}</span>}
                      <span style={{ display: "block", fontSize: 11, color: "#6b7280" }}>
                        {!reachable ? "No e-mail address — there is nowhere to send it."
                          : notifiedLine(r, zone)}
                      </span>
                    </span>
                  </label>
                );
              })}
            </div>
            {first ? (
              <MessagePreview
                title={`How it will arrive — for ${first.full_name || first.email}`}
                deps={[groupId, doc.id, first.user_id, first.project_id]}
                fetcher={() => api.post(`/api/groups/${groupId}/documents/${doc.id}/notify/preview`,
                                        { user_id: first.user_id, project_id: first.project_id ?? null })} />
            ) : (
              <p style={{ margin: 0, fontSize: 12, color: "#6b7280" }}>Tick somebody to see the message.</p>
            )}
          </>
        )}
        <SheetActions>
          <Btn variant="secondary" onClick={onClose}>Cancel</Btn>
          {rows && rows.length > 0 && (
            <Btn onClick={send} disabled={busy || chosen.length === 0}>
              {busy ? <><Spinner /> Sending…</> : `Send to ${chosen.length}`}
            </Btn>
          )}
        </SheetActions>
    </Sheet>
  );
}

// ── Ask for approval (2026-09-30) ────────────────────────────────────────────
//
// A version or a change waiting for a second admin reached nobody until one
// happened to open Personal Administration. The list is the server's — every
// global admin but the author, the rule approving applies — and the server
// checks the ticked ids against it again on send. The mail leads to Personal
// Administration and never carries the decision: approving needs the
// approver's own sign-in. `item` is `{ version, change_id }`, exactly one set.
function askedLine(r, zone) {
  const sent = r.last_asked
    ? `Last asked ${formatWhen(r.last_asked, { zone })}` : "Never asked about this";
  if (r.pending_notification === "queued") {
    return r.last_asked ? `${sent}; a newer one is queued, not sent yet`
      : "A request is queued, not sent yet";
  }
  if (r.pending_notification === "failed") return `${sent}; the newest one failed to send`;
  return sent;
}

function ApprovalRequestDialog({ groupId, document: doc, item, onSent, onClose }) {
  const zone = useDisplayTimezone();
  const fourEyes = useFourEyes();
  const [data, setData] = useState(null);
  const [ticked, setTicked] = useState(() => new Set());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const version = item.version ?? null;
  const changeId = item.change_id ?? null;
  const query = version != null ? `version=${version}` : `change_id=${changeId}`;
  const base = `/api/groups/${groupId}/documents/${doc.id}/approval-request`;

  useEffect(() => {
    api.get(`${base}?${query}`)
      .then(out => {
        setData(out);
        // Ticked by default — everybody who may approve it and can be sent it.
        setTicked(new Set(out.recipients.filter(canReach).map(r => r.user_id)));
      })
      .catch(e => { setData({ recipients: [] }); setError(e.message); });
  }, [base, query]);

  const rows = data ? data.recipients : null;
  const toggle = r => setTicked(t => {
    const next = new Set(t);
    if (next.has(r.user_id)) next.delete(r.user_id); else next.add(r.user_id);
    return next;
  });
  const chosen = (rows || []).filter(r => ticked.has(r.user_id));
  const first = chosen[0];
  const what = data?.item?.what || (version != null ? `version ${version}` : "a change to it");

  const send = async () => {
    setBusy(true); setError(null);
    try {
      const out = await api.post(base, { version, change_id: changeId,
                                         user_ids: chosen.map(r => r.user_id) });
      onSent(out.queued);
    } catch (e) { setError(e.message); } finally { setBusy(false); }
  };

  return (
    <Sheet labelledBy={`ask-${doc.id}-t`} onClose={onClose} maxWidth={620}>
        <h2 id={`ask-${doc.id}-t`} style={{ margin: "0 0 8px", fontSize: 18, fontWeight: 700, color: "#0b6b33" }}>
          Ask for approval: {doc.title}
        </h2>
        <p style={{ margin: "0 0 12px", fontSize: 13, color: "#374151" }}>
          Waiting for approval: <strong>{what}</strong>
          {data?.item?.requested_by
            ? <>, from <span data-break>{personText(data.item.requested_by)}</span></> : null}. Everybody listed
          may approve it. Each person ticked gets one e-mail with a link to Personal Administration,
          where they approve or reject it under their own sign-in — the mail itself approves
          nothing. The wording is the <em>Policy waiting for approval</em> template in E-mail and
          templates.
        </p>
        {error && <div role="alert" style={{ marginBottom: 12, padding: "10px 12px", borderRadius: 8, background: "rgba(220,38,38,0.06)", border: "1px solid rgba(220,38,38,0.25)", color: "#b91c1c", fontSize: 13 }}>{error}</div>}
        {rows === null ? <div style={{ color: "#6b7280" }}><Spinner /> Loading…</div>
          : rows.length === 0 ? (
            !error && (
              <p style={{ margin: 0, fontSize: 13, color: "#92400e" }}>
                {fourEyes ? (
                  <>There is no second administrator who could approve it: nobody may approve what they
                  composed or asked for themselves. An administrator has to be added first.</>
                ) : (
                  <>There is nobody else to ask. Four eyes on documents is switched off, so you can
                  approve this yourself under Personal Administration.</>
                )}
              </p>
            )
          ) : (
          <>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 8 }}>
              <Btn variant="secondary" style={{ padding: "3px 10px", fontSize: 12 }}
                   onClick={() => setTicked(new Set(rows.filter(canReach).map(r => r.user_id)))}>Select all</Btn>
              <Btn variant="secondary" style={{ padding: "3px 10px", fontSize: 12 }}
                   onClick={() => setTicked(new Set())}>Select none</Btn>
            </div>
            <div style={{ border: "1px solid #e5e7eb", borderRadius: 8, maxHeight: 240, overflowY: "auto", marginBottom: 14 }}>
              {rows.map(r => {
                const reachable = canReach(r);
                return (
                  <label key={r.user_id}
                         style={{ display: "flex", gap: 10, alignItems: "flex-start", padding: "7px 10px", borderBottom: "1px solid #f3f4f6", fontSize: 13, cursor: reachable ? "pointer" : "default", color: reachable ? "#1a1a1a" : "#6b7280" }}>
                    <input type="checkbox" checked={ticked.has(r.user_id)} disabled={!reachable}
                           onChange={() => toggle(r)} style={{ marginTop: 2 }} />
                    <span data-break>
                      <strong>{r.full_name || r.email || `User ${r.user_id}`}</strong>
                      {reachable && <span style={{ color: "#6b7280" }}> · {r.email}</span>}
                      <span style={{ display: "block", fontSize: 11, color: "#6b7280" }}>
                        {!reachable ? "No e-mail address — there is nowhere to send it."
                          : askedLine(r, zone)}
                      </span>
                    </span>
                  </label>
                );
              })}
            </div>
            {first ? (
              <MessagePreview
                title={`How it will arrive — for ${first.full_name || first.email}`}
                deps={[groupId, doc.id, version, changeId, first.user_id]}
                fetcher={() => api.post(`${base}/preview`,
                                        { version, change_id: changeId, user_id: first.user_id })} />
            ) : (
              <p style={{ margin: 0, fontSize: 12, color: "#6b7280" }}>Tick somebody to see the message.</p>
            )}
          </>
        )}
        <SheetActions>
          <Btn variant="secondary" onClick={onClose}>Cancel</Btn>
          {rows && rows.length > 0 && (
            <Btn onClick={send} disabled={busy || chosen.length === 0}>
              {busy ? <><Spinner /> Sending…</> : `Send to ${chosen.length}`}
            </Btn>
          )}
        </SheetActions>
    </Sheet>
  );
}

const EMPTY_DRAFT = { title: "", category: "info", mode: "notice", blocking: false,
                      rules: DEFAULT_RULES, project_ids: [], affiliation_ids: [] };

// What the create form posts. `confirm_rules` is what the server reads;
// `scope` is written to agree with it, so the legacy column never describes a
// different policy from the rules on the same row. `audience` is retired.
// Targets go only with a per-project rule — the server refuses them otherwise.
function draftPayload(draft) {
  const { rules: chosen, ...rest } = draft;
  const rules = draft.mode === "acknowledge" ? chosen : DEFAULT_RULES;
  const list = rulesAsList(rules);
  const narrowed = draft.mode === "acknowledge" && hasPerProject(rules);
  return {
    ...rest,
    confirm_rules: list,
    scope: narrowed ? "per_project" : "group",
    project_ids: narrowed ? draft.project_ids : [],
    affiliation_ids: narrowed ? draft.affiliation_ids : [],
  };
}

export default function DocumentsPanel({ groupId, canEdit }) {
  const zone = useDisplayTimezone();
  const fourEyes = useFourEyes();
  const { user, refreshDuties } = useAuth();
  const [docs, setDocs] = useState(null);
  const [editing, setEditing] = useState(null);
  const [tracking, setTracking] = useState(null);
  const [draft, setDraft] = useState(EMPTY_DRAFT);
  // What this group actually has. Both are group-scoped by the server —
  // `Project.group_id` and `Affiliation.group_id` — and the route refuses an id
  // from another group anyway, because a picker is not an authority.
  const [retargeting, setRetargeting] = useState(null);
  const [projects, setProjects] = useState(null);
  const [affiliations, setAffiliations] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const load = () => api.get(`/api/groups/${groupId}/documents`).then(setDocs)
    .catch(e => setError(e.message));
  // `load` is redefined every render; listing it would refetch on
  // every keystroke elsewhere in the component.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { setDocs(null); setEditing(null); load(); }, [groupId]);
  useEffect(() => {
    if (!canEdit) return;
    setProjects(null); setAffiliations(null);
    api.get(`/api/projects?group_id=${groupId}`).then(setProjects).catch(() => setProjects([]));
    api.get(`/api/affiliations?group_id=${groupId}`).then(setAffiliations).catch(() => setAffiliations([]));
  }, [groupId, canEdit]);

  const create = async () => {
    setBusy(true); setError(null);
    try {
      const made = await api.post(`/api/groups/${groupId}/documents`, draftPayload(draft));
      setDraft(EMPTY_DRAFT);
      await load();
      setEditing(made);
    } catch (e) { setError(e.message); } finally { setBusy(false); }
  };

  // The lifecycle. Each transition is its own verb and its own audit entry —
  // `is_active` used to be one field in a general-purpose PATCH, so switching a
  // binding policy off looked exactly like renaming it in the log.
  const [confirming, setConfirming] = useState(null);   // { doc, to }
  const [history, setHistory] = useState(null);
  const [notifying, setNotifying] = useState(null);
  const [asking, setAsking] = useState(null);           // { doc, item }
  const [queuedNote, setQueuedNote] = useState(null);

  const move = async () => {
    const { doc, to } = confirming;
    setBusy(true);
    setError(null);
    try {
      if (to === "deleted") {
        await api.delete(`/api/groups/${groupId}/documents/${doc.id}`);
      } else {
        await api.post(`/api/groups/${groupId}/documents/${doc.id}/archive`, {});
      }
      setConfirming(null);
      await load();
    } catch (e) { setError(e.message); } finally { setBusy(false); }
  };

  // The asker takes their own pending change back (2026-10-30), with a reason.
  // Its own route: a group admin cannot reach `/api/admin/document-changes/…`,
  // and the server admits only the asker there. Not *Withdraw*, which on this
  // row archives the policy itself.
  const takeBack = async doc => {
    const reason = window.prompt(
      "Why are you taking this request back? The log keeps the reason.");
    if (!(reason || "").trim()) return;
    setBusy(true); setError(null);
    try {
      await api.post(
        `/api/groups/${groupId}/documents/${doc.id}/changes/${doc.pending_change.id}/retract`,
        { reason: reason.trim() });
      await load();
      refreshDuties?.();
    } catch (e) { setError(e.message); } finally { setBusy(false); }
  };

  // What FileLink does for the PDF on the row, for the copy in the menu: a
  // saved file, not a navigation, which would carry no Authorization header.
  const saveEvidence = async doc => {
    setError(null);
    try {
      await api.saveFile(`/api/groups/${groupId}/documents/${doc.id}/pdf?signatures=all`);
    } catch (e) {
      // api.js already handles an expired session by signing the user out.
      if (e.name !== "AuthExpiredError") setError(e.message || "Could not fetch the file");
    }
  };

  const showTracking = async doc => {
    setError(null);
    try {
      setTracking({ doc, data: await api.get(
        `/api/groups/${groupId}/documents/${doc.id}/acknowledgements`) });
    } catch (e) { setError(e.message); }
  };

  return (
    <Card>
      <h3 style={{ margin: "0 0 6px", fontSize: 16, fontWeight: 700 }}>Documents</h3>
      <p style={{ margin: "0 0 14px", color: "#6b7280", fontSize: 13 }}>
        Policies, terms and information this facility publishes. A notice is simply readable;
        one set to <strong>acknowledge</strong> asks the people named to confirm they have read
        it, and records that the same way a signed quotation is recorded.
      </p>

      {error && <div role="alert" style={{ marginBottom: 12, padding: "10px 12px", borderRadius: 8, background: "rgba(220,38,38,0.06)", border: "1px solid rgba(220,38,38,0.25)", color: "#b91c1c", fontSize: 13 }}>{error}</div>}
      {queuedNote && <div role="status" style={{ marginBottom: 12, padding: "10px 12px", borderRadius: 8, background: "rgba(0,150,64,0.06)", border: "1px solid rgba(0,150,64,0.25)", color: "#0b6b33", fontSize: 13 }}>{queuedNote}</div>}

      {docs === null ? <div style={{ color: "#6b7280" }}><Spinner /> Loading…</div>
        : docs.length === 0 ? <p style={{ color: "#6b7280", fontSize: 13, margin: "0 0 14px" }}>No documents yet.</p>
        : (
        <table className="responsive-table"
               style={{ borderCollapse: "collapse", width: "100%", fontSize: 13, marginBottom: 16 }}>
          <thead>
            <tr style={{ borderBottom: "2px solid #e5e7eb", textAlign: "left" }}>
              <th style={{ padding: "8px 10px" }}>Title</th>
              <th style={{ padding: "8px 10px" }}>Kind</th>
              <th style={{ padding: "8px 10px" }}>Version</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {docs.map(d => (
              <tr key={d.id} style={{ borderBottom: "1px solid #f3f4f6" }}>
                <td data-label="Title" style={{ padding: "8px 10px" }}>
                  {d.title}
                  {/* Named targets, none of which reaches anything — every
                      project it named has been deleted, or its affiliation has
                      no projects under it. NOT the same as withdrawn: it is
                      still active, still in force, and asked of nobody. Said
                      here because the alternative is a row that looks exactly
                      like one reaching the whole facility. */}
                  {d.reaches_nobody && (
                    <div style={{ marginTop: 3 }}>
                      <Badge color="amber">Reaches nobody</Badge>
                      <span style={{ color: "#6b7280", fontSize: 12 }}>
                        {" "}every project and affiliation it names is gone — it is in force and
                        asked of no one
                      </span>
                    </div>
                  )}
                </td>
                <td data-label="Kind" style={{ padding: "8px 10px" }}>
                  <Badge color={d.mode === "acknowledge" ? "amber" : "gray"}>
                    {d.mode === "acknowledge" ? "Must be acknowledged" : "Notice"}
                  </Badge>
                  {d.mode === "acknowledge" && (
                    <span style={{ color: "#6b7280", fontSize: 12 }}> · {describeRules(rulesOf(d))}</span>
                  )}
                  {/* **The rule, in one line.** Who a policy reaches used to be
                      spread across scope, audience and two pickers, so reading
                      it meant combining four controls in your head. */}
                  <div style={{ marginTop: 3, fontSize: 12, color: "#6b7280" }}>
                    {APPLICATION_LABELS[d.application || "groups"]}
                  </div>
                </td>
                <td data-label="Version" style={{ padding: "8px 10px", color: "#6b7280" }}>
                  {/* `version` is the one IN FORCE, so a document whose versions
                      are all waiting for approval used to render "not published"
                      — over six stored versions, on dev. What is pending is said
                      beside it, never instead of it. */}
                  {d.version ? `v${d.version}` : (d.pending ? "none in force" : "not published")}
                  {d.pending && (
                    <div style={{ marginTop: 2 }}>
                      <Badge color="amber">v{d.pending.version} waiting for approval</Badge>
                      {/* The honest answer to "why is this stuck": there is
                          nobody else. A fact about the facility, not about the
                          version, so it is said here rather than left to a 403
                          on a screen nobody reached. */}
                      {/* While four eyes is off the author may approve their own
                          leftover, so "nobody may" would be untrue there. */}
                      {fourEyes && d.pending.eligible_approvers === 0 && (
                        <div style={{ fontSize: 11, color: "#92400e", marginTop: 2, maxWidth: 260 }}>
                          There is no second administrator who could approve it — you composed it,
                          and nobody may approve their own. Add one, and this becomes approvable.
                        </div>
                      )}
                      {!fourEyes && (
                        <div style={{ fontSize: 11, color: "#92400e", marginTop: 2, maxWidth: 260 }}>
                          Left from before four eyes was switched off. Any administrator, you
                          included, can approve it under Personal Administration.
                        </div>
                      )}
                    </div>
                  )}
                  {/* "Uploaded file", not "PDF": the download link in this same
                      row is already labelled PDF, and every document here can
                      be downloaded as one. What this badge says is where the
                      document CAME FROM, which is what decides whether it can
                      be edited or compared. */}
                  {d.source_kind === "pdf" && (
                    <> <Badge color="blue">Uploaded file</Badge></>
                  )}
                  {/* The state, said rather than inferred from a missing row.
                      `active` carries no badge: it is the ordinary case, and a
                      badge on every line says nothing. */}
                  {d.status === "archived" && (
                    <> <Badge color="gray">Withdrawn</Badge></>
                  )}
                </td>
                {/* The primary actions on the row — PDF, and Send notification
                    where somebody can be waiting — and the rest behind More…
                    (2026-09-30). Nine buttons here were 24px tall and, at
                    390px, started at x=680: off the screen. data-row-actions
                    is what gives what stays 44px on a phone. */}
                <td style={{ padding: "8px 10px" }}>
                  {/* A wrapper, not the cell: a flex <td> leaves table layout. */}
                  <div data-row-actions style={{ display: "flex", gap: 6, alignItems: "center",
                                                 justifyContent: "flex-end", flexWrap: "wrap" }}>
                    {d.version && (
                      <FileLink path={`/api/groups/${groupId}/documents/${d.id}/pdf`} />
                    )}
                    {/* Only where somebody can be waiting: a published document
                        that asks for a confirmation and is still in force. The
                        server refuses the rest in words anyway. */}
                    {canEdit && d.mode === "acknowledge" && d.version && d.status !== "archived" && (
                      <Btn variant="secondary" onClick={() => { setQueuedNote(null); setNotifying(d); }}
                           style={{ padding: "4px 10px", fontSize: 12 }}>Send notification</Btn>
                    )}
                    {/* *Ask for approval* where something waits for a second
                        admin: the pending version first, else the pending
                        change. With both, the change is in the menu. */}
                    {canEdit && (d.pending || d.pending_change) && (
                      <Btn variant="secondary"
                           onClick={() => { setQueuedNote(null); setAsking({ doc: d, item: d.pending
                             ? { version: d.pending.version, change_id: null }
                             : { version: null, change_id: d.pending_change.id } }); }}
                           style={{ padding: "4px 10px", fontSize: 12 }}>Ask for approval</Btn>
                    )}
                    <RowActionsMenu context={d.title} items={[
                      canEdit && d.pending && d.pending_change &&
                        { label: "Ask for approval of the change", onSelect: () => {
                            setQueuedNote(null);
                            setAsking({ doc: d, item: { version: null, change_id: d.pending_change.id } });
                          } },
                      // Only the person who asked, by id — refusing anybody
                      // else's request stays a reviewer's decision.
                      canEdit && d.pending_change && user?.id != null
                        && d.pending_change.requested_by_id === user.id &&
                        { label: "Take back my request", onSelect: () => takeBack(d) },
                      // Both sides of any comparison have always been in the
                      // database — versions are inserted, never edited — and
                      // until 2026-09-08 nothing could show them. `versions`,
                      // not `version`: the first is how many exist and the
                      // second is the one in force. Gating on the latter hid the
                      // history — and with it the only place to approve — from
                      // exactly the documents that needed it.
                      d.versions > 1 && { label: "History", onSelect: () => setHistory(d) },
                      d.mode === "acknowledge" && d.version &&
                        { label: "Who has read it", onSelect: () => showTracking(d) },
                      canEdit && { label: "Who it is for", onSelect: () => setRetargeting(d) },
                      canEdit && { label: d.source_kind === "pdf" ? "New version" : "Edit text",
                                   onSelect: () => setEditing(d) },
                      // The evidence copy, for whoever may hold all of it. The
                      // server refuses it through the same helper that decides
                      // the panel's list, so this item appearing and the
                      // download succeeding cannot disagree.
                      d.version && d.mode === "acknowledge" && canEdit &&
                        { label: "PDF + signatures", onSelect: () => saveEvidence(d) },
                      // Withdrawal is final: there is no way back into force from
                      // here, and the server refuses the transition as well. A
                      // facility that wants the policy again publishes a new
                      // version, which is also the only path that runs the
                      // two-person approval.
                      canEdit && d.status !== "archived" &&
                        { label: "Withdraw", onSelect: () => setConfirming({ doc: d, to: "archived" }) },
                      // No longer refused because people have signed — decided on
                      // 2026-09-09. The signatures go with it, which is why the
                      // dialog counts them and offers Withdraw at the moment of
                      // the act rather than only in this tooltip.
                      canEdit && { label: "Delete", tone: "danger",
                                   onSelect: () => setConfirming({ doc: d, to: "deleted" }),
                                   title: d.signatures > 0
                                     ? `Destroys this document and the ${d.signatures} ${d.signatures === 1 ? "signature" : "signatures"} on it. Withdraw keeps them.`
                                     : "Remove a document nobody has signed" },
                    ]} />
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {/* A sheet, not a panel inside the list it describes (2026-09-30). */}
      {tracking && (
        <Sheet labelledBy={`tracking-${tracking.doc.id}-t`} onClose={() => setTracking(null)} maxWidth={620}>
          <SheetHeader id={`tracking-${tracking.doc.id}-t`}
                       title={`${tracking.doc.title} · v${tracking.data.version}`}
                       onClose={() => setTracking(null)} />
          {/* Said, not left to be inferred. The server answers this route per
              reader — an operator sees their own signature and those of people
              with less standing, never another operator's — so a list rendered
              without this note reads as "nobody else has signed". */}
          {tracking.data.partial && (
            <div style={{ marginTop: 10, padding: "8px 10px", borderRadius: 6, background: "rgba(217,119,6,0.08)", border: "1px solid rgba(217,119,6,0.3)", fontSize: 12, color: "#92400e" }}>
              This list is not complete. You can see your own signature and those of people with
              less standing in this group; a group administrator sees all of them.
            </div>
          )}
          <div style={{ marginTop: 10, fontSize: 13, overflowWrap: "anywhere" }}>
            <strong>Confirmed ({tracking.data.confirmed.length})</strong>
            {tracking.data.confirmed.map((c, i) => (
              <div key={i} style={{ color: "#374151" }}>
                <PersonLabel name={c.name} email={c.email} /> · {formatWhen(c.at, { zone })}{c.ip ? ` · ${c.ip}` : ""}
                {/* This could never fire until 2026-09-08: the route returned
                    only the current version's rows, so every one of them
                    matched by construction. It now says WHICH version. */}
                {!c.matches_current && (
                  <span style={{ color: "#b45309" }}>
                    {" "}· signed v{c.signed_version ?? "?"}, not the version in force
                  </span>
                )}
              </div>
            ))}
            <div style={{ marginTop: 10 }}>
              <strong>Still outstanding ({tracking.data.outstanding.length})</strong>
              {tracking.data.outstanding.map((o, i) => (
                <div key={i} style={{ color: "#92400e" }}>
                  <PersonLabel name={o.full_name} email={o.email} />{o.project ? ` · ${o.project}` : ""}
                </div>
              ))}
            </div>
          </div>
        </Sheet>
      )}

      {notifying && (
        <NotifyDialog groupId={groupId} document={notifying} onClose={() => setNotifying(null)}
                      onSent={n => {
                        setNotifying(null);
                        setQueuedNote(`${n} ${n === 1 ? "notification" : "notifications"} queued for ${notifying.title}.`);
                      }} />
      )}

      {asking && (
        <ApprovalRequestDialog groupId={groupId} document={asking.doc} item={asking.item}
                               onClose={() => setAsking(null)}
                               onSent={n => {
                                 setAsking(null);
                                 setQueuedNote(`${n} ${n === 1 ? "administrator" : "administrators"} asked to approve ${asking.doc.title}.`);
                               }} />
      )}

      {history && (
        <VersionHistory groupId={groupId} document={history} onClose={() => setHistory(null)}
                        onAsk={canEdit ? n => {
                          // One sheet at a time: the history closes, the ask opens.
                          setHistory(null); setQueuedNote(null);
                          setAsking({ doc: history, item: { version: n, change_id: null } });
                        } : undefined} />
      )}

      {confirming && (
        <ConfirmDialog
          title={confirming.to === "deleted" ? `Delete "${confirming.doc.title}"?`
                 : `Withdraw "${confirming.doc.title}"?`}
          confirmLabel={confirming.to === "deleted"
                          ? (fourEyes ? "Send delete for approval" : "Delete now")
                          : (fourEyes ? "Send withdrawal for approval" : "Withdraw now")}
          tone={confirming.to === "deleted" ? "danger" : "default"}
          confirmWord={confirming.to === "deleted" ? confirming.doc.title : undefined}
          busy={busy}
          onConfirm={move}
          onClose={() => setConfirming(null)}
        >
          {/* **Said at the moment of the act**, not in a tooltip. Since
              2026-09-09 neither of these happens when you press the button:
              they are proposed, and a second administrator decides. Somebody
              who thinks a document is gone and finds it still listed will
              press again. */}
          {fourEyes ? (
            <p style={{ margin: "0 0 10px", fontSize: 13, color: "#b45309" }}>
              <strong>This is a request.</strong> A second administrator has to agree before
              anything happens, and until then the document is unchanged. You will find it under
              Personal Administration.
            </p>
          ) : (
            <p style={{ margin: "0 0 10px", fontSize: 13, color: "#b45309" }}>
              <strong>This happens at once.</strong> Four eyes on documents is switched off, so no
              second administrator is asked; the log records that the rule was off.
            </p>
          )}
          {confirming.to === "archived" && (
            <p style={{ margin: 0, fontSize: 13, color: "#374151" }}>
              New projects will not be asked for it. Every version and every signature is kept, and a
              project that already agreed to it keeps that obligation — withdrawing a policy does not
              rewrite what work already under way agreed to.
            </p>
          )}
          {confirming.to === "deleted" && (
            <p style={{ margin: 0, fontSize: 13, color: "#374151" }}>
              {confirming.doc.signatures > 0 ? (
                <>
                  This destroys the document, every version of it, and the{" "}
                  <strong>{confirming.doc.signatures}{" "}
                    {confirming.doc.signatures === 1 ? "signature" : "signatures"}</strong>{" "}
                  already given. Nothing can be brought back; the only surviving record is one entry
                  in the audit log naming everybody who signed. <strong>Withdraw keeps them</strong>{" "}
                  and takes the document out of force.
                </>
              ) : (
                <>Nobody has signed this document, so there is no evidence to lose. It disappears
                  from every list and cannot be brought back.</>
              )}
            </p>
          )}
        </ConfirmDialog>
      )}

      {retargeting && (
        <TargetEditor groupId={groupId} document={retargeting}
                      projects={projects} affiliations={affiliations}
                      onSaved={() => { setRetargeting(null); load(); }}
                      onCancel={() => setRetargeting(null)} />
      )}

      {editing && (
        <DocumentEditor groupId={groupId} document={editing}
                        onSaved={() => load()} onCancel={() => setEditing(null)} />
      )}

      {canEdit && !editing && (
        <>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 12, alignItems: "end" }}>
            <Field label="New document">
              <Input value={draft.title} onChange={e => setDraft(d => ({ ...d, title: e.target.value }))}
                     placeholder="Terms and conditions" />
            </Field>
            <Field label="Category">
              <Select options={CATEGORIES} value={draft.category}
                      onChange={e => setDraft(d => ({ ...d, category: e.target.value }))} />
            </Field>
            <Field label="Must be acknowledged"
                   hint="Off means it is simply published for people to read">
              <Toggle checked={draft.mode === "acknowledge"}
                      onChange={v => setDraft(d => ({ ...d, mode: v ? "acknowledge" : "notice" }))} />
            </Field>
            {draft.mode === "acknowledge" && (
              <Field label="Blocks new work"
                     hint="Refuses to START an upload or a delivery until it is confirmed. Transfers already running are never interrupted.">
                <Toggle checked={draft.blocking}
                        onChange={v => setDraft(d => ({ ...d, blocking: v }))} />
              </Field>
            )}
          </div>

          {/* Who it is for: one rule per role, and beneath it what the
              per-project rules reach. Replaced the role checkboxes and the
              "Confirmed" select on 2026-09-29 — those could say one cadence for
              everybody, and the usage policy is once for an operator and on
              every project for a researcher.

              **A notice has neither**: it asks nobody, and its targets never
              reached anything — `_documents_for_project` filters on
              `mode == "acknowledge"`, so a notice is found only through the
              standing list, which has no project to filter against. */}
          {draft.mode === "acknowledge" ? (
            <div style={{ marginTop: 12 }}>
              <RoleRules name="rules-new" rules={draft.rules}
                         onChange={rules => setDraft(d => ({ ...d, rules }))} />
              <PerProjectTargets
                rules={draft.rules} projects={projects} affiliations={affiliations}
                projectIds={draft.project_ids} affiliationIds={draft.affiliation_ids}
                projectHint="A LIST. A project created after this is not on it — you would have to come back and add it."
                affiliationHint="A RULE. A project created later under one of these is covered on the day it is made, without anybody adding it."
                noAffiliations="This group has imported no price list, so it has no affiliations."
                onToggleProject={id => setDraft(d => ({
                  ...d,
                  project_ids: d.project_ids.includes(id)
                    ? d.project_ids.filter(x => x !== id) : [...d.project_ids, id],
                }))}
                onToggleAffiliation={id => setDraft(d => ({
                  ...d,
                  affiliation_ids: d.affiliation_ids.includes(id)
                    ? d.affiliation_ids.filter(x => x !== id) : [...d.affiliation_ids, id],
                }))} />
            </div>
          ) : (
            <p style={{ fontSize: 12, color: "#6b7280", margin: "12px 0 0" }}>
              A notice is readable by <strong>everybody in this group</strong> and asks nobody to
              confirm it. Switch on <em>Must be acknowledged</em> to say who confirms, and how often.
            </p>
          )}

          <div style={{ marginTop: 12 }}>
            <Btn onClick={create} disabled={busy || !draft.title.trim()}>
              {busy ? <><Spinner /> Working…</> : "Create document"}
            </Btn>
          </div>
        </>
      )}
    </Card>
  );
}
